import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  ChevronDown,
  Calendar,
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  BarChart,
  Download,
  Star,
  Clock
} from 'lucide-react';
import { getRegionsData, getStoresData } from '../../data/mockData';

// Helper components
const RegionCard = ({ region, isActive, onClick }) => {
  const getProgressColor = (score) => {
    if (score >= 85) return 'bg-emerald-500';
    if (score >= 75) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  return (
    <div 
      onClick={onClick}
      className={`p-4 rounded-xl border cursor-pointer transition-all ${
        isActive 
          ? 'bg-blue-50 border-blue-200 shadow-sm' 
          : 'bg-white hover:bg-gray-50 border-gray-200'
      }`}
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
          <MapPin className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
            {region.name}
          </h3>
          <p className={`text-xs ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
            {region.storeCount} Stores
          </p>
        </div>
        <div className={`text-lg font-bold ${isActive ? 'text-blue-700' : 'text-gray-700'}`}>
          {region.score}
        </div>
      </div>

      <div className="mt-3">
        <div className="relative pt-1">
          <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-100">
            <div 
              style={{ width: `${region.score}%` }}
              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${getProgressColor(region.score)}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const CategoryComparisonCard = ({ regions, category, maxScore, color, icon: Icon }) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center mb-4">
        <div className={`p-2 rounded-lg ${color.bg}`}>
          <Icon className={`h-5 w-5 ${color.text}`} />
        </div>
        <h3 className="ml-3 text-lg font-medium text-gray-900">{category}</h3>
        <div className="ml-auto text-sm text-gray-500">/{maxScore}</div>
      </div>
      
      <div className="space-y-3">
        {regions.map((region) => {
          const score = (() => {
            switch(category) {
              case 'Sales Performance': return region.salesScore;
              case 'Google Reviews': return region.reviewsScore;
              case 'Maintenance': return region.maintenanceScore;
              case 'Audit Score': return region.auditScore;
              case 'Speed of Service': return region.speedScore;
              case 'Quality Control': return region.qualityScore;
              default: return 0;
            }
          })();
          
          const percentage = (score / maxScore) * 100;
          
          return (
            <div key={region.id} className="flex items-center">
              <div className="w-24 text-sm font-medium text-gray-900 truncate">
                {region.name.replace(' Region', '')}
              </div>
              <div className="ml-2 flex-1">
                <div className="relative h-4 overflow-hidden rounded-full bg-gray-100 flex">
                  <div 
                    style={{ width: `${percentage}%` }}
                    className={`h-full ${color.bar}`}
                  />
                </div>
              </div>
              <div className="ml-2 w-12 text-right text-sm font-semibold">
                {score}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TopStoreCard = ({ regionId, stores }) => {
  // Filter and sort stores for the selected region
  const regionStores = stores
    .filter(store => store.region.toLowerCase().includes(regionId))
    .sort((a, b) => b.totalScore - a.totalScore)
    .slice(0, 5);
  
  // Helper to get score color
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-rose-600';
  };

  // Helper to get background color for score
  const getScoreBgColor = (score) => {
    if (score >= 85) return 'bg-emerald-100';
    if (score >= 70) return 'bg-amber-100';
    return 'bg-rose-100';
  };
    
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h3 className="text-lg font-medium text-gray-900">Top Stores</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {regionStores.map((store, idx) => (
          <div key={store.id} className="flex items-center p-3 hover:bg-gray-50 transition-colors">
            <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-medium bg-blue-100 text-blue-800">
              {idx + 1}
            </div>
            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">{store.name}</p>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${getScoreBgColor(store.totalScore)} ${getScoreColor(store.totalScore)}`}>
                  {store.totalScore}
                </div>
              </div>
              <p className="text-xs text-gray-500">{store.city}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RegionComparison = () => {
  const [regions, setRegions] = useState([]);
  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [comparisonPeriod, setComparisonPeriod] = useState('month');
  
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const regionsData = await getRegionsData();
      const storesData = await getStoresData();
      
      setRegions(regionsData);
      setStores(storesData);
      setSelectedRegion(regionsData[0].id);
      setIsLoading(false);
    };
    
    loadData();
  }, []);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Sort regions by score
  const sortedRegions = [...regions].sort((a, b) => b.score - a.score);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <Link 
              to="/scorecard"
              className="mr-4 p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-semibold">Region Comparison</h1>
              <p className="text-blue-100 mt-1">Compare performance across regions</p>
            </div>
          </div>
          <div className="relative inline-block text-left">
            <div>
              <button 
                type="button" 
                className="inline-flex justify-center w-full rounded-md border border-white/20 shadow-sm px-4 py-2 bg-white/10 backdrop-blur-sm text-sm font-medium text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                id="period-menu-button"
              >
                <Calendar className="mr-2 h-5 w-5 text-white/80" />
                {comparisonPeriod === 'day' && 'Today'}
                {comparisonPeriod === 'week' && 'This Week'}
                {comparisonPeriod === 'month' && 'This Month'}
                {comparisonPeriod === 'quarter' && 'This Quarter'}
                {comparisonPeriod === 'year' && 'This Year'}
                <ChevronDown className="ml-2 -mr-1 h-5 w-5 text-white/80" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Region Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {sortedRegions.map((region) => (
          <RegionCard 
            key={region.id}
            region={region}
            isActive={selectedRegion === region.id}
            onClick={() => setSelectedRegion(region.id)}
          />
        ))}
      </div>
      
      {/* Selected Region Dashboard */}
      {selectedRegion && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Region Stats */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {regions.find(r => r.id === selectedRegion).name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Performance Metrics</p>
            </div>
            
            <div className="space-y-6">
              <div className="flex justify-between items-baseline">
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Overall Score</p>
                  <div className="text-3xl font-bold text-gray-900">
                    {regions.find(r => r.id === selectedRegion).score}
                  </div>
                </div>
                <div className={`flex items-center text-sm font-medium ${
                  regions.find(r => r.id === selectedRegion).change > 0 
                    ? 'text-emerald-600' 
                    : 'text-rose-600'
                }`}>
                  {regions.find(r => r.id === selectedRegion).change > 0 ? (
                    <TrendingUp className="h-4 w-4 mr-1" />
                  ) : (
                    <TrendingUp className="h-4 w-4 mr-1" />
                  )}
                  {Math.abs(regions.find(r => r.id === selectedRegion).change)}%
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Total Stores</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    {regions.find(r => r.id === selectedRegion).storeCount}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Network Rank</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    #{sortedRegions.findIndex(r => r.id === selectedRegion) + 1}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Top Store</div>
                  <div className="text-xl font-bold text-gray-900 mt-1 truncate">
                    {regions.find(r => r.id === selectedRegion).topStore}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Avg. Rating</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">
                    4.2/5
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Score Breakdown</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Sales Performance</span>
                    <span className="font-medium text-gray-900">
                      {regions.find(r => r.id === selectedRegion).salesScore}/30
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Google Reviews</span>
                    <span className="font-medium text-gray-900">
                      {regions.find(r => r.id === selectedRegion).reviewsScore}/20
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Maintenance</span>
                    <span className="font-medium text-gray-900">
                      {regions.find(r => r.id === selectedRegion).maintenanceScore}/10
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Audit Score</span>
                    <span className="font-medium text-gray-900">
                      {regions.find(r => r.id === selectedRegion).auditScore}/10
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Speed of Service</span>
                    <span className="font-medium text-gray-900">
                      {regions.find(r => r.id === selectedRegion).speedScore}/15
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Quality Control</span>
                    <span className="font-medium text-gray-900">
                      {regions.find(r => r.id === selectedRegion).qualityScore}/15
                    </span>
                  </div>
                </div>
              </div>
              
              <button className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <Download className="h-4 w-4 mr-2" />
                Export Region Report
              </button>
            </div>
          </div>
          
          {/* Top Stores */}
          <div className="h-full flex flex-col">
            <TopStoreCard regionId={selectedRegion} stores={stores} />
          </div>
          
          {/* Placeholder Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Performance Trend</h3>
              <div className="flex space-x-1">
                <button className="p-1 rounded hover:bg-gray-100">
                  <ArrowLeft className="h-4 w-4 text-gray-400" />
                </button>
                <button className="p-1 rounded hover:bg-gray-100">
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="h-[240px] relative">
              <svg className="absolute inset-0" viewBox="0 0 100 100" preserveAspectRatio="none">
                {/* Background grid */}
                <line x1="0" y1="20" x2="100" y2="20" stroke="#E5E7EB" strokeWidth="0.5" />
                <line x1="0" y1="40" x2="100" y2="40" stroke="#E5E7EB" strokeWidth="0.5" />
                <line x1="0" y1="60" x2="100" y2="60" stroke="#E5E7EB" strokeWidth="0.5" />
                <line x1="0" y1="80" x2="100" y2="80" stroke="#E5E7EB" strokeWidth="0.5" />
                
                {/* Chart lines */}
                <path 
                  d="M0,60 L9,55 L18,50 L27,48 L36,52 L45,45 L54,40 L63,35 L72,30 L81,32 L90,25 L100,20" 
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
                <path 
                  d="M0,65 L9,62 L18,60 L27,58 L36,62 L45,57 L54,55 L63,50 L72,52 L81,49 L90,45 L100,40" 
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeDasharray="4 2"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2 text-xs text-gray-500">
                <span>Jan</span>
                <span>Feb</span>
                <span>Mar</span>
                <span>Apr</span>
                <span>May</span>
                <span>Jun</span>
              </div>
            </div>
            <div className="flex items-center justify-center mt-4 space-x-6">
              <div className="flex items-center">
                <div className="h-3 w-3 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-700">{regions.find(r => r.id === selectedRegion).name}</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 w-3 bg-emerald-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-700">Network Average</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Category Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <CategoryComparisonCard 
          regions={sortedRegions}
          category="Sales Performance"
          maxScore={30}
          color={{
            bg: 'bg-blue-100',
            text: 'text-blue-600',
            bar: 'bg-blue-500'
          }}
          icon={BarChart}
        />
        <CategoryComparisonCard 
          regions={sortedRegions}
          category="Google Reviews"
          maxScore={20}
          color={{
            bg: 'bg-amber-100',
            text: 'text-amber-600',
            bar: 'bg-amber-500'
          }}
          icon={Star}
        />
        <CategoryComparisonCard 
          regions={sortedRegions}
          category="Speed of Service"
          maxScore={15}
          color={{
            bg: 'bg-emerald-100',
            text: 'text-emerald-600',
            bar: 'bg-emerald-500'
          }}
          icon={Clock}
        />
      </div>
    </div>
  );
};

export default RegionComparison; 